import React, { useEffect, useState } from "react";
import { IonModal, IonContent, IonIcon } from "@ionic/react";
import { closeCircleOutline } from "ionicons/icons";
import { TermsOfUseContent } from "../../pages/settings/help/Terms";
import {
  PRIVACY_POLICY_KEY,
  PrivacyPolicyContent,
} from "../../pages/settings/help/PrivacyPolicy";
import { Preferences } from "@capacitor/preferences";
import { fetchDocumentByType } from "../../services/documents";

// ✅ Shared interface for both documents
interface LegalDocument {
  id: number;
  type: string;
  version: string;
  effective_date: string;
  last_updated: string;
  title: string;
  content_markdown: string;
  content_html: string;
  is_current: boolean;
  created_at: string;
  updated_at: string;
}

export const TERMS_OF_USE_KEY = "termsOfUse";

interface TermsAgreementModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  conditions?: "terms" | "privacy" | "";
}

const TermsAgreementModal: React.FC<TermsAgreementModalProps> = ({
  isOpen,
  onClose,
  conditions = "",
  title = "Terms & Privacy Agreement",
}) => {
  const [documentData, setDocumentData] = useState<LegalDocument | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // ✅ Unified loader for both documents
  useEffect(() => {
    const loadDocument = async (type: "privacy" | "terms") => {
      setIsLoading(true);
      try {
        const key = type === "privacy" ? PRIVACY_POLICY_KEY : TERMS_OF_USE_KEY;

        // 1️⃣ Try loading from local cache
        const localResult = await Preferences.get({ key });
        let localDoc: LegalDocument | null = null;

        if (localResult.value) {
          localDoc = JSON.parse(localResult.value);
          setDocumentData(localDoc);
          setIsLoading(false); // show cached version immediately
        }

        // 2️⃣ Fetch remote content
        const remoteDoc = await fetchDocumentByType(type);

        // 3️⃣ Update cache only if new or missing
        if (!localDoc || localDoc.updated_at !== remoteDoc.updated_at) {
          await Preferences.set({
            key,
            value: JSON.stringify(remoteDoc),
          });
          setDocumentData(remoteDoc);
        }
      } catch (error) {
        console.error(`Failed to load ${type} document:`, error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen && conditions) {
      loadDocument(conditions);
    }
  }, [conditions, isOpen]);

  return (
    <IonModal
      isOpen={!!conditions && isOpen}
      onDidDismiss={onClose}
      breakpoints={[0, 0.5, 0.8, 1]}
      initialBreakpoint={0.95}
      handleBehavior="cycle"
      className="rounded-t-3xl"
    >
      {/* Header Section */}
      <div
        style={{
          background: "#fff",
          padding: "20px 20px 0",
          position: "relative",
          textAlign: "center",
        }}
      >
        <h2
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: "#000",
            margin: "0 0 10px",
          }}
        >
          {conditions === "terms" ? "Terms of Use" : "Privacy Policy"}
        </h2>

        <IonIcon
          onClick={onClose}
          style={{
            position: "absolute",
            top: "15px",
            right: "15px",
            fontSize: "18px",
            color: "#05ACD9",
            cursor: "pointer",
            width: "26px",
            height: "26px",
          }}
          icon={closeCircleOutline}
        />
      </div>

      {/* Content Section */}
      <IonContent
        className="ion-padding"
        style={{ "--background": "#F7F5FA", paddingBottom: "80px" }}
      >
        {conditions === "terms" ? (
          // Render Terms
          <TermsOfUseContent      html={documentData?.content_html}
            isLoading={isLoading} />
        ) : (
          // Render Privacy Policy
          <PrivacyPolicyContent
            html={documentData?.content_html}
            isLoading={isLoading}
          />
        )}
      </IonContent>
    </IonModal>
  );
};

export default TermsAgreementModal;
